import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TableSortLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  LocalOffer as TopItemIcon,
  ReceiptLong as TotalItemsIcon,
  Fastfood,
  Class,
  Clear
} from '@mui/icons-material';

import outletService from '../services/outletService';
import { getMenuPerformanceData, downloadExcelGetMenuPerformanceData, downloadPDFGetMenuPerformanceData } from '../services/reportService';
import { useNotification } from '../contexts/NotificationContext';
import menuService from '../services/menuService';

const MenuItemPerformanceReport = () => {

  const { showSuccess, showError } = useNotification()

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [outlets, setOutlets] = useState([]);
  const [categories, setCategories] = useState([])
  const [selectedOutlet, setSelectedOutlet] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [reportData, setReportData] = useState({
    result: [],
    top_menu_item: null,
    top_category: null
  });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [menuLength, setMenuLength] = useState(0);
  const [clearFilterShow, setClearFilterShow] = useState(false)

  function checkFilter() {
    return search === '' && selectedCategory === 'all' && selectedOutlet === "all" && startDate === "" && endDate === ""
  }

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'quantity',
    direction: 'desc'
  });

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  useEffect(() => {
    fetchOutlets();
    fetchCategories()
  }, []);

  useEffect(() => {
    fetchReportData();
    if (!checkFilter()) {
      setClearFilterShow(true)
    }
  }, [selectedOutlet, startDate, endDate, selectedCategory, sortConfig]);

  const fetchCategories = async () => {
    try {
      const data = await menuService.getCategories();
      setCategories(data);
    } catch (err) {
      // console.error('Failed to fetch categories:', err);
    }
  }

  const fetchOutlets = async () => {
    try {
      const response = await outletService.getOutlets();
      setOutlets(response);
      const adminOutletId = JSON.parse(localStorage.getItem('admin_outlet_id'));
      if (adminOutletId) {
        setSelectedOutlet(adminOutletId);
      }
    } catch (error) {
      // console.error("Error fetching outlets:", error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await getMenuPerformanceData(selectedOutlet, startDate, endDate, selectedCategory, sortConfig.key, sortConfig.direction);
      // console.log("Response in Report => ", response.result.length)
      setReportData(response);
      setMenuLength(response.result.length);
      setLoading(false);
    } catch (error) {
      // console.error("Error fetching rep/ort data:", error);
      setLoading(false);
    }
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    const items = reportData.result || [];
    const totalQuantity = items.reduce((sum, item) => sum + (item.count || 0), 0);
    const totalSales = items.reduce((sum, item) => sum + (item.total_price || 0), 0);

    return {
      totalQuantity,
      totalSales,
      totalItems: items.length
    };
  };

  const summary = calculateSummary();
  const topItem = reportData.top_menu_item;
  const topCategory = reportData.top_category;

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort data
  const filteredData = (reportData.result || [])
    .filter(item =>
      search === '' ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.category_name.toLowerCase().includes(search.toLowerCase())
    )
  // .sort((a, b) => {
  //   if (sortConfig.key === 'name') {
  //     return sortConfig.direction === 'asc' 
  //       ? a.name.localeCompare(b.name) 
  //       : b.name.localeCompare(a.name);
  //   } else if (sortConfig.key === 'category_name') {
  //     return sortConfig.direction === 'asc' 
  //       ? a.category_name.localeCompare(b.category_name) 
  //       : b.category_name.localeCompare(a.category_name);
  //   } else if (sortConfig.key === 'count') {
  //     return sortConfig.direction === 'asc' 
  //       ? a.count - b.count 
  //       : b.count - a.count;
  //   } else if (sortConfig.key === 'total_price') {
  //     return sortConfig.direction === 'asc' 
  //       ? a.total_price - b.total_price 
  //       : b.total_price - a.total_price;
  //   }
  //   return 0;
  // });

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      await downloadExcelGetMenuPerformanceData(selectedOutlet, startDate, endDate, selectedCategory, sortConfig.key, sortConfig.direction);
      showSuccess("Excel Sheet Downloaded Successfully")
    } catch (error) {
      // console.error("Error downloading Excel:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await downloadPDFGetMenuPerformanceData(selectedOutlet, startDate, endDate, selectedCategory, sortConfig.key, sortConfig.direction);
      showSuccess("PDF Downloaded Successfully")
    } catch (error) {
      // console.error("Error downloading PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleClearFilter = async () => {
    setStartDate('')
    setEndDate('')
    setSelectedOutlet('all')
    setSearch('')
    setSelectedCategory('all')
    // Reset sort to default
    setSortConfig({ key: 'count', direction: 'desc' });
    setClearFilterShow(false)
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Menu Item Performance Report
          </Typography>
          <Typography color="text.secondary" variant="body1">
            Analyze menu items based on sales, quantity sold and contribution
          </Typography>
        </Box>

        {/* Export Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={20} /> : <ExcelIcon />}
            onClick={handleDownloadExcel}
            disabled={downloading}
          >
            Download Excel
          </Button>
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
            onClick={handleDownloadPDF}
            disabled={downloading}
          >
            Download PDF
          </Button>
          {/* <Button
            variant="contained"
            startIcon={<Clear />}
            onClick={handleClearFilter}
          >
            Clear Filter
          </Button> */}
        </Box>
      </Box>

      {/* Four Summary Cards */}
      {paginatedData.length === 0 ? <>
        <Typography variant='h1' sx={{ textAlign: 'center', mb: 2 }}>No Records Found</Typography>
      </> :
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3} sx={{ mb: 2 }} >
            <Card sx={{ bgcolor: '#e3f2fd', height: '100%', borderRadius: '6px' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Fastfood sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Best Selling Item
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {reportData?.top_menu_item?.menu_item_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sales : S$ {reportData?.top_menu_item?.total_amount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ mb: 2 }} >
            <Card sx={{ bgcolor: '#e8f5e9', height: '100%', borderRadius: '6px' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Class sx={{ color: '#2e7d32', mr: 1 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Top Category
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {reportData?.top_category?.category_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revenue Generated : S$ {reportData?.top_category?.total_amount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ mb: 2 }}>
            <Card sx={{ bgcolor: '#fff3e0', height: '100%', borderRadius: '6px' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ color: '#ed6c02', mr: 1 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Highest Quantity Sold
                  </Typography>
                </Box>
                <Typography variant="h2" component="div" fontWeight="bold" noWrap>
                  {reportData?.top_menu_item?.occurrences}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reportData?.top_menu_item?.menu_item_name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ mb: 2 }}>
            <Card sx={{ bgcolor: '#f3e5f5', height: '100%', borderRadius: '6px' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon sx={{ color: '#9c27b0', mr: 1 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Top Category Sales
                  </Typography>
                </Box>
                <Typography variant="h2" component="div" fontWeight="bold">
                  S$ {reportData?.top_category?.total_amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reportData?.top_category?.category_name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>}

      {/* Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Outlet</InputLabel>
              <Select
                value={selectedOutlet}
                label="Outlet"
                onChange={(e) => setSelectedOutlet(e.target.value)}
                disabled={!!JSON.parse(localStorage.getItem('admin_outlet_id'))}
                startAdornment={
                  <InputAdornment position="start">
                    <StorefrontIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Outlets</MenuItem>
                {outlets.map((outlet) => (
                  <MenuItem key={outlet.id} value={outlet.id}>{outlet.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (endDate && e.target.value > endDate) {
                  setEndDate('');
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split('T')[0]
              }}
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={!startDate}
              inputProps={{
                min: startDate || undefined,
                max: new Date().toISOString().split('T')[0]
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <StorefrontIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by item or category..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if(!checkFilter()) setClearFilterShow(true)
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {clearFilterShow &&
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilter}
              >
                Clear Filter
              </Button>
            </Grid>}
        </Grid>
      </Box>

      {/* Data Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 500, borderRadius: '6px' }}>
          <Table stickyHeader sx={{ borderRadius: '6px' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">#</Typography>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'menu_item_name'}
                    direction={sortConfig.key === 'menu_item_name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('menu_item_name')}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">Item Name</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'category_name'}
                    direction={sortConfig.key === 'category_name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('category_name')}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">Category</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortConfig.key === 'quantity'}
                    direction={sortConfig.key === 'quantity' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('quantity')}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">Quantity Sold</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortConfig.key === 'total'}
                    direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">Total Sales (S$)</Typography>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No data found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((item, index) => {
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.category_name}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{item.count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                        S$ {item.total_price.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        />
      </Paper>
    </Box>
  );
};

export default MenuItemPerformanceReport;